import { FC, useContext, useEffect, useState } from 'react';
import FSMSubscriptionModal from './components/FSMSubscriptionModal';
import { selectCurrentTeam, selectIsOwner } from 'containers/app/appSlice';
import { useAppDispatch, useAppSelector } from 'hooks';
import { FSMPlan } from 'containers/integrations/fsm-integration/constants';
import FSMNotAvaialbleModal from './components/FSMNotAvaialbleModal';
import FSMPlanComparisonModal from './components/FSMPlanComparisonModal';
import { FSM_PLANS } from './constants';
import UpgradeFSMModal from './components/UpgradeFSMModal';
import SubscribedModal from './components/SubscribedModal';
import routesPath from 'routes/RoutesPath';
import { useCommunicationsControl } from 'containers/communications';
import { useLocation, useNavigate } from 'react-router-dom';
import { isFSMLiteRoute, isFSMPlusRoute, shouldShowFSMModal } from './utils';
import { FSMSubscriptionIcon } from 'assets/icons';
import { resetCreateJobState } from 'containers/jobs/create-edit-job/createJobSlice';
import { useConfirmFsmV2SubscriptionMutation } from 'containers/integrations/fsm-integration/api';
import FSMSubscriptionEndedModal from './components/FSMSubscriptionEndedModal';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { DefaultPrivateLayoutContextProps } from 'routes/types';
import { DefaultPrivateLayoutContext } from 'routes/DefaultPrivateLayout';
import { useUpdateFsmV2SubscriptionMutation } from 'containers/integrations/fsm-integration/api';
import UpgradeFromAppInfoModal from 'containers/integrations/fsm-integration/components/UpgradeFromAppInfoModal';
import ConfirmUpgradeToFSMPlus from 'containers/integrations/fsm-integration/components/ConfirmUpgradeToFSMPlus';

const GlobalModalRenderer: FC = () => {
  const navigate = useNavigate();
  const { allowAction: allowJobCreate } = useCommunicationsControl('JOB_CREATE');
  const location = useLocation();

  const { isFullScreenLayout } = useContext<DefaultPrivateLayoutContextProps>(DefaultPrivateLayoutContext);

  const isOwner = useAppSelector(selectIsOwner);
  const { id: teamId } = useAppSelector(selectCurrentTeam);
  const dispatch = useAppDispatch();
  const showSubscriptionModal = shouldShowFSMModal(location.pathname) ?? false;

  const {
    isSystemDowngraded,
    hasDowngradedAfterTrial,
    isTrialEnded,
    isUserCancelled,
    fsmLitePlan,
    fsmPlusPlan,
    isNoPlan,
    isFSMLitePlan,
    isFSMPlusPlan,
    currentFSMPlan,
    isSubscriptionConfirmed,
    isSubscriptionFromWeb,
    hasExpiredPlan,
    isInFSMGracePeriod,
    migratedTeamWithJobs,
    source,
    isPaymentMethodUpdated,
    collectPaymentMethodInfoAfterConfirmation
  } = useFSMPlanInfo();

  const [confirmFSMSubscription, { isLoading: isConfirmSubscriptionLoading }] = useConfirmFsmV2SubscriptionMutation();
  const [updateFsmV2Subscription, { isLoading: isUpdateFsmV2SubscriptionLoading }] =
    useUpdateFsmV2SubscriptionMutation();

  const [showModal, setShowModal] = useState(true);
  const [showFSMComparePlansModal, setShowFSMComparePlansModal] = useState(false);
  const [showSubscribedModal, setShowSubscribedModal] = useState(false);
  const [showUpgradeFromAppInfoModal, setShowUpgradeFromAppInfoModal] = useState(false);
  const [showConfirmUpgradeToFSMPlusModal, setShowConfirmUpgradeToFSMPlusModal] = useState(false);

  const isDowngraded = isSystemDowngraded || hasDowngradedAfterTrial;

  useEffect(() => {
    setShowModal(true);
  }, [currentFSMPlan]);

  /**
   * Confirms a plan subscription (FSM Lite or FSM Plus)
   */
  const confirmPlanOperation = async (planDetails: typeof fsmLitePlan | typeof fsmPlusPlan): Promise<void> => {
    await confirmFSMSubscription({
      teamId,
      body: { trialDuration: planDetails?.trialDuration ?? 0, planCode: planDetails?.priceId ?? '' }
    }).unwrap();
  };

  /**
   * Upgrades to FSM Plus plan
   * Returns: { needsPaymentMethod: boolean }
   */
  const upgradeToPlusPlan = async (): Promise<{ needsPaymentMethod: boolean }> => {
    // Case 1: Not confirmed and no payment method - confirm first
    if (!isSubscriptionConfirmed && !isPaymentMethodUpdated) {
      await confirmPlanOperation(fsmPlusPlan);
      return { needsPaymentMethod: fsmPlusPlan?.trialDuration === 0 };
    }

    // Case 2a: Confirmed but needs to collect payment method after confirmation
    // Confirm first, then navigate to payment collection
    if (collectPaymentMethodInfoAfterConfirmation && hasDowngradedAfterTrial) {
      await confirmPlanOperation(fsmPlusPlan);
      return { needsPaymentMethod: true };
    }

    // Case 2b: Confirmed but payment method not updated - update subscription with plan code
    if (isSubscriptionConfirmed && !isPaymentMethodUpdated) {
      await updateFsmV2Subscription({
        teamId,
        body: { planCode: fsmPlusPlan?.priceId ?? '', cancel: false, resume: false }
      }).unwrap();
      return { needsPaymentMethod: true };
    }

    // Case 3: On Lite and cancelled - resume first, then upgrade
    if (isFSMLitePlan && isUserCancelled) {
      await updateFsmV2Subscription({
        teamId,
        body: { cancel: false, resume: true }
      }).unwrap();
    }

    // Case 4: Standard upgrade
    await updateFsmV2Subscription({
      teamId,
      body: { cancel: false, resume: false, planCode: fsmPlusPlan?.priceId ?? '' }
    }).unwrap();

    return { needsPaymentMethod: false };
  };

  /**
   * Confirms or updates FSM Lite plan
   * Returns: { needsPaymentMethod: boolean }
   */
  const confirmOrUpdateLitePlan = async (): Promise<{ needsPaymentMethod: boolean }> => {
    // Case 1: Not confirmed - confirm first
    if (!isSubscriptionConfirmed) {
      await confirmPlanOperation(fsmLitePlan);
      return { needsPaymentMethod: fsmLitePlan?.trialDuration === 0 };
    }

    // Case 2a: Confirmed but needs to collect payment method after confirmation
    // Confirm first, then navigate to payment collection
    if (collectPaymentMethodInfoAfterConfirmation && hasDowngradedAfterTrial) {
      await confirmPlanOperation(fsmLitePlan);
      return { needsPaymentMethod: true };
    }

    // Case 2b: Confirmed but payment method not updated - update subscription with plan code
    if (isSubscriptionConfirmed && !isPaymentMethodUpdated) {
      await updateFsmV2Subscription({
        teamId,
        body: { cancel: false, resume: false, planCode: fsmLitePlan?.priceId ?? '' }
      }).unwrap();
      return { needsPaymentMethod: true };
    }

    // Case 3: Standard update
    await updateFsmV2Subscription({
      teamId,
      body: { cancel: false, resume: false, planCode: fsmLitePlan?.priceId ?? '' }
    }).unwrap();

    return { needsPaymentMethod: false };
  };

  /**
   * Navigates to FieldServiceManagement page and opens payment modal
   */
  const navigateToPaymentCollection = () => {
    navigate(routesPath.FIELD_SERVICE_MANAGEMENT, { state: { openPaymentModal: true } });
  };

  const handleChoosePlanClick = () => {
    setShowFSMComparePlansModal(true);
  };

  const handleCloseFSMComparePlansModal = () => {
    setShowFSMComparePlansModal(false);
  };

  const handleStartTrial = () => {
    if (isFSMLitePlan && source && !isSubscriptionFromWeb && !hasExpiredPlan) {
      setShowUpgradeFromAppInfoModal(true);
      return;
    }

    setShowConfirmUpgradeToFSMPlusModal(true);
    setShowModal(false);
  };

  const handleConfirmUpgradeToFSMPlus = async () => {
    try {
      const result = await upgradeToPlusPlan();
      if (result.needsPaymentMethod) {
        navigateToPaymentCollection();
      }
      setShowConfirmUpgradeToFSMPlusModal(false);
    } catch (error) {
      //
    }
  };

  const handleCloseConfirmUpgradeToFSMPlusModal = () => {
    setShowConfirmUpgradeToFSMPlusModal(false);
    setShowModal(true);
  };

  const handleCloseUpgradeFromAppInfoModal = () => {
    setShowUpgradeFromAppInfoModal(false);
  };

  const handleChoosePlan = async (plan: string) => {
    try {
      if (plan === FSM_PLANS.FIELD_SERVE_PLUS) {
        if (isFSMLitePlan && source && !isSubscriptionFromWeb && !hasExpiredPlan) {
          setShowUpgradeFromAppInfoModal(true);
          return;
        }
        const result = await upgradeToPlusPlan();
        if (result.needsPaymentMethod) {
          navigateToPaymentCollection();
          return;
        }
      } else {
        const result = await confirmOrUpdateLitePlan();
        if (result.needsPaymentMethod) {
          navigateToPaymentCollection();
          return;
        }
      }
      handleCloseFSMComparePlansModal();
      if (currentFSMPlan === FSMPlan.NO_PLAN) {
        setShowModal(false);
        setShowSubscribedModal(true);
      }
    } catch (error) {
      // Handle error - subscribed modal will not be shown when confirmation fails
    }
  };

  const handleCreateJobClick = () => {
    if (!allowJobCreate()) {
      handleCloseSubscribedModal();
      return;
    }
    dispatch(resetCreateJobState());
    navigate(routesPath.CREATE_JOB);
    handleCloseSubscribedModal();
  };

  const handleCloseSubscribedModal = () => {
    setShowSubscribedModal(false);
  };

  const goToPreviousPage = () => {
    navigate(-1);
  };

  const handleCloseSubscriptionEndedModal = () => {
    if (isFullScreenLayout) {
      navigate(-1);
    }
  };

  const handleRenewSubscription = () => {
    if (isOwner) {
      navigate(routesPath.FIELD_SERVICE_MANAGEMENT);
    } else {
      handleCloseSubscriptionEndedModal();
    }
  };

  const shouldShowSubscriptionEndedModal = (): boolean => {
    if (!isDowngraded || !showModal) {
      return false;
    }

    const isLiteRoute = isFSMLiteRoute(location.pathname);
    const isPlusRoute = isFSMPlusRoute(location.pathname);

    if (isFSMLitePlan && isLiteRoute) {
      return true;
    }

    if (isFSMPlusPlan && (isPlusRoute || isLiteRoute)) {
      return true;
    }

    return false;
  };

  if (migratedTeamWithJobs && isInFSMGracePeriod && !showSubscriptionModal) {
    return null;
  }

  if (showSubscriptionModal || showSubscribedModal) {
    if (isOwner) {
      return (
        <>
          {isNoPlan && showModal && (
            <FSMSubscriptionModal
              onChoosePlan={handleChoosePlanClick}
              showCloseButton={isFullScreenLayout}
              handleClose={goToPreviousPage}
            />
          )}
          {isFSMLitePlan && showModal && isFSMPlusRoute(location.pathname) && (
            <UpgradeFSMModal
              onComparePlans={handleChoosePlanClick}
              onStartTrial={handleStartTrial}
              isLoading={isConfirmSubscriptionLoading || isUpdateFsmV2SubscriptionLoading}
            />
          )}
          {showFSMComparePlansModal && showModal && (
            <FSMPlanComparisonModal
              handleClose={handleCloseFSMComparePlansModal}
              onChoosePlan={handleChoosePlan}
              isLoading={isConfirmSubscriptionLoading}
              currentFSMPlan={currentFSMPlan}
            />
          )}
          {showSubscribedModal && (
            <SubscribedModal
              title="You're all set!"
              description={
                <div className="text-sbase font-medium text-primaryText">
                  Field Serve is now active for your business.
                  <div className="mt-5"> Let's create your first job.</div>
                </div>
              }
              primaryButtonText="Create new job"
              secondaryButtonText="Done"
              primaryButtonClick={handleCreateJobClick}
              onClose={handleCloseSubscribedModal}
              icon={<FSMSubscriptionIcon />}
            />
          )}
          {shouldShowSubscriptionEndedModal() && (
            <FSMSubscriptionEndedModal
              handleClose={handleCloseSubscriptionEndedModal}
              onRenew={handleRenewSubscription}
              showCloseButton={isFullScreenLayout}
              isFullScreenModal={false}
            />
          )}
          {showUpgradeFromAppInfoModal && <UpgradeFromAppInfoModal handleClose={handleCloseUpgradeFromAppInfoModal} />}
          {showConfirmUpgradeToFSMPlusModal && (
            <ConfirmUpgradeToFSMPlus
              handleClose={handleCloseConfirmUpgradeToFSMPlusModal}
              onStartTrial={handleConfirmUpgradeToFSMPlus}
              isLoading={isConfirmSubscriptionLoading || isUpdateFsmV2SubscriptionLoading}
              onBack={handleCloseConfirmUpgradeToFSMPlusModal}
            />
          )}
        </>
      );
    }
    if (
      (isUserCancelled || isTrialEnded) &&
      ((isFSMLitePlan && isFSMLiteRoute(location.pathname)) || (isFSMPlusPlan && isFSMPlusRoute(location.pathname)))
    ) {
      return (
        <FSMSubscriptionEndedModal
          handleClose={handleCloseSubscriptionEndedModal}
          onRenew={handleRenewSubscription}
          showCloseButton={false}
          isFullScreenModal={false}
        />
      );
    }
    return <FSMNotAvaialbleModal showCloseButton={isFullScreenLayout} handleClose={goToPreviousPage} />;
  }
  return null;
};

export default GlobalModalRenderer;
